import { useCallback, useState } from 'react'
import Modal from '../Modal/Modal'
import { IUserReport } from '../../helpers/Interfaces';
import { convertDuration, validDateField, validStringField } from '../../helpers/HelperFunctions';

import "./ViewUserReport.scss"

type ViewUserReportProps = {
    report: IUserReport;
    open: boolean;
    closeModal: CallableFunction;
}

function ViewUserReport(props: Readonly<ViewUserReportProps>) {
    const [showAlert, setShowAlert] = useState({ isError: false, message: "", open: false });

    const closeAlert = useCallback(() => {
        let newShowAlert = showAlert;
        newShowAlert.open = false;
        setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
    }, [])

    const handleClose = useCallback((shouldUpdate: boolean) => {
        props.closeModal("view", shouldUpdate);
    }, [])

    function DataView(header: string, data: string) {
        return (<div className="data-view">
            <span className="header">{header}</span>
            <span className="data">{data}</span>
        </div>)
    }

    function CommentView(header: string, data: string) {
        return (<div className="data-view">
            <span className="header">{header}</span>
            <span className="comment" style={{ whiteSpace: "pre-wrap" }}>{data}</span>
        </div>)
    }

    function stageData(stage: 'scribe' | 'proof' | 'review') {
        const selectedStage = props.report.stageDetails[stage];

        return (<div className='stage'>
            <span className='title'>{stage.charAt(0).toUpperCase() + stage.slice(1)} - {selectedStage.status}</span>
            <div className='stage-data'>
                <div className='column'>
                    <span className='header'>Allocation</span>
                    <span className='data'>{selectedStage.assignee}</span>
                </div>
                <div className='column'>
                    <span className='header'>Allocated Date</span>
                    <span className='data'>{validDateField(selectedStage.allocatedDate)}</span>
                </div>
                <div className='column'>
                    <span className='header'>Completed Date</span>
                    <span className='data'>{validDateField(selectedStage.completedDate)}</span>
                </div>
                {selectedStage.status === "Completed"
                    ? <div className='column'>
                        <span className='header'>Time Taken</span>
                        <span className='data'>{convertDuration(selectedStage.totalDuration)}</span>
                    </div>
                    : (<></>)
                }
            </div>
        </div>)
    }

    function getReportTitle(report: IUserReport) {
        if (report.reportName) {
            return report.reportName;
        } else if (report.patientName && report.reportType && report.clinicName) {
            return `${report.patientName} - ${report.reportType} - ${report.clinicName}`
        }

        return "View Report";
    }

    return (<Modal showAlert={showAlert} closeAlert={closeAlert} editable={false} open={props.open} close={handleClose}
        styles={"view-report-card"} >
        <div className="view-user-report-container">
            <span className='title'>{getReportTitle(props.report)}</span>
            <div className="report-view">
                <div className='content'>
                    <div className="row">
                        {DataView("Patient Name", validStringField(props.report.patientName))}
                        {DataView("Doctor", validStringField(props.report.doctorName))}
                        {DataView("Clinic", validStringField(props.report.clinicName))}
                        {DataView("Report Type", validStringField(props.report.reportType))}
                        {DataView("Created Date", validDateField(props.report.createdDate))}
                        {DataView("Due Date", validDateField(props.report.dueDate))}
                        {DataView("Place of Assessment", validStringField(props.report.placeOfAssessment))}
                        {DataView("Date of Assessment", validDateField(props.report.dateOfAssessment))}
                        {DataView("Priority", validStringField(props.report.priority))}
                        {DataView("Status", validStringField(props.report.status))}
                        {DataView("Provider Number", validStringField(props.report.providerNumber))}
                        {(DataView("Submitted Date", validDateField(props.report.submittedDate)))}
                        {(DataView("Completed Date", validDateField(props.report.completedDate)))}
                        {DataView("Referrer", validStringField(props.report.referrer))}
                        {DataView("Duration", convertDuration(props.report.totalDuration))}
                        {DataView("Report Name", validStringField(props.report.reportName))}
                    </div>
                    <div className="row">
                        {CommentView("Comments", validStringField(props.report.notes))}
                    </div>
                </div>
                <div className='stage-details'>
                    {props.report.stageDetails.scribe != null
                        ? stageData("scribe")
                        : (<></>)
                    }
                    {props.report.stageDetails.proof != null
                        ? (stageData("proof"))
                        : (<></>)
                    }
                    {props.report.stageDetails.review != null
                        ? (stageData("review"))
                        : (<></>)
                    }
                </div>
            </div>
        </div>
    </Modal>)
}

export default ViewUserReport